import { XSidebar } from '@adornis/chemistry/elements/components/x-sidebar.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { computed, Signal } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';

const pageHeaderAdditionals = new Signal(new Map<number, Renderable>());

export const addPageHeaderAdditional = (index: number, content: Renderable) => {
  const valueMap = new Map(pageHeaderAdditionals.value);
  valueMap.set(index, content);
  pageHeaderAdditionals.value = valueMap;
};

export const removePageHeaderAdditional = (index: number) => {
  const valueMap = new Map(pageHeaderAdditionals.value);
  valueMap.delete(index);
  pageHeaderAdditionals.value = valueMap;
};

export type PageHeaderProps = {
  // the main title
  title: Renderable;

  // additional content to the right of the title
  rightContent?: Renderable;
  // subtitle below the title. can describe the page
  subtitle?: Renderable;
  // whether to show a shadow
  shadow?: boolean;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  onTitleClick?: () => void;
};

export const PageHeader = stateful((props: PageHeaderProps) => {
  const isTitleClickable = typeof props.onTitleClick === 'function';

  const additionalContent = computed(() => {
    return [...pageHeaderAdditionals.value.entries()].sort((a, b) => a[0] - b[0]).map(([, content]) => content);
  });

  return html`
    <div class="navbar sticky top-0 z-100 min-h-0 shrink-0 flex-col items-stretch p-0">
      <div class="flex items-center justify-between gap-3 p-1 md:gap-4 md:px-4 md:py-2">
        <div class="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
          <button
            class="btn btn-ghost btn-sm btn-square"
            type="button"
            aria-label="Menü öffnen"
            @click=${() => XSidebar.toggle()}
          >
            <span class="icon-[material-symbols--menu-rounded] inline-block h-5 w-5"></span>
          </button>
          ${
            props.showBackButton
              ? html`
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    type="button"
                    aria-label="Zurück"
                    @click=${props.onBack ? props.onBack : () => history.back()}
                  >
                    <span class="icon-[material-symbols--arrow-back-rounded] inline-block h-5 w-5"></span>
                  </button>
                `
              : nothing
          }

          <div class="min-w-0">
            ${
              isTitleClickable
                ? html`
                    <h1
                      @click=${props.onTitleClick}
                      class="text-xl font-semibold text-base-content hover:text-primary cursor-pointer"
                    >
                      ${props.title}
                    </h1>
                  `
                : html`<h1 class="text-xl font-semibold text-base-content">${props.title}</h1>`
            }
            ${
              props.subtitle
                ? html`<p class="mt-1 truncate text-sm text-base-content/70">${props.subtitle}</p>`
                : nothing
            }
          </div>
        </div>

        ${props.rightContent ? html`<div class="shrink-0">${props.rightContent}</div>` : nothing}
      </div>
      ${
        // by position, not keyed by the content: pages re-add their header content on every route change,
        // a content key rebuilt it each time and the lifted tabs lost the transition between two tabs
        additionalContent.value
      }
    </div>
  `;
});
